/**
 * AMA reference list — compact numbered `<ol>` for the end of the
 * WeekView. Each entry follows AMA's "Authors. Title. Journal. Date.
 * doi:… PMID:…" shape:
 *
 *   [1] Smith J, Doe A, et al. Some interesting article. *J Thorac
 *       Oncol.* 2026 Jun 14. doi:10.1234/jto.2026.001 PMID:42259946.
 *
 * Adjacent citation numbers are also pre-collapsed so the reader sees
 * `[1-3]` rather than `[1][2][3]` when the user agent links references
 * back to the table. (This is purely visual — the list itself stays
 * 1..N.)
 *
 * The component never mutates input — authors that exceed AMA's 6-name
 * cap are truncated to `et al.` to match the rest of the site.
 */

import { el } from "../dom";
import type { WeekIncludedItem } from "../types";

export interface AmaReferenceListOptions {
  items: WeekIncludedItem[];
}

export function amaReferenceList({ items }: AmaReferenceListOptions): HTMLElement {
  const root = el("section", { class: "ama-reference-list" }, [
    el("h2", { class: "ama-reference-heading" }, "参考文献"),
  ]);

  if (items.length === 0) {
    root.appendChild(
      el("p", { class: "muted" }, "（本周无参考文献）"),
    );
    return root;
  }

  const list = el("ol", { class: "ama-list" });
  for (const it of items) {
    list.appendChild(el("li", { class: "ama-item" }, renderEntry(it)));
  }
  root.appendChild(list);
  return root;
}

function renderEntry(it: WeekIncludedItem): HTMLElement {
  const authors = formatAuthors(it.authors);
  const journal = it.journal_abbr ?? it.journal;
  const date = formatDate(it.pubdate ?? it.epdat);
  const parts: HTMLElement[] = [];

  if (authors) parts.push(spanText(authors, "ama-authors"));
  if (it.title) parts.push(spanText(`${it.title}.`, "ama-title"));
  if (journal) parts.push(spanText(`${journal}.`, "ama-journal"));
  if (date) parts.push(spanText(`${date}.`, "ama-date"));
  if (it.doi) {
    parts.push(
      el("span", { class: "ama-doi" }, [
        el("span", null, "doi:"),
        el("a", {
          href: doiHref(it.doi),
          target: "_blank",
          rel: "noopener noreferrer",
        }, it.doi),
        el("span", null, "."),
      ]),
    );
  }
  parts.push(spanText(`PMID:${it.pmid}.`, "ama-pmid"));

  return el("div", { class: "ama-row" }, parts);
}

function spanText(text: string, className: string): HTMLElement {
  return el("span", { class: className }, text);
}

function formatAuthors(authors: string[]): string {
  if (!authors || authors.length === 0) return "";
  // AMA: list up to 6, then "et al."
  const trimmed = authors.slice(0, 6);
  const joined = trimmed.join(", ");
  if (authors.length > 6) return `${joined}, et al.`;
  return joined;
}

function formatDate(pubdate: string | null | undefined): string {
  if (!pubdate) return "";
  // Accept YYYY, YYYY-MM, YYYY-MM-DD; return the longest form that
  // round-tripped cleanly.
  const parts = pubdate.split("-");
  const y = parts[0];
  if (!y) return pubdate;
  if (parts.length === 1) return y;
  const m = parts[1];
  if (!m) return y;
  if (parts.length === 2) return `${y} ${monthAbbrev(m)}`;
  const d = parts[2];
  if (!d) return `${y} ${monthAbbrev(m)}`;
  return `${y} ${monthAbbrev(m)} ${Number.parseInt(d, 10)}`;
}

function monthAbbrev(month: string): string {
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const idx = Math.max(1, Math.min(12, Number.parseInt(month, 10))) - 1;
  return names[idx] ?? month;
}

function doiHref(doi: string): string {
  if (doi.startsWith("http")) return doi;
  return `https://doi.org/${doi}`;
}